<template>
  <div class="decision-intelligence">
    <!-- 二级 Tab：供应链决策智能体的四个模块 -->
    <div class="sub-tabs">
      <div
        v-for="tab in tabs"
        :key="tab.key"
        class="sub-tab-item"
        :class="{ active: activeKey === tab.key }"
        @click="handleTabClick(tab.key)">
        <i :class="tab.icon"></i>
        <span>{{ tab.title }}</span>
      </div>
    </div>

    <!-- 正文：模块第一次点开才建 iframe，之后只切显隐。
         四个一起建会同时加载四个重页面；每次切换都重建又会丢掉模块内部状态
         （筛选、滚动、已算出的结果），所以按需挂载 + 之后保活。 -->
    <div class="sub-tab-body" v-loading="!loaded[activeKey]" element-loading-text="加载中...">
      <iframe
        v-for="tab in mountedTabs"
        v-show="activeKey === tab.key"
        :key="tab.key"
        :src="tab.url"
        class="di-frame"
        frameborder="0"
        allowfullscreen
        @load="handleFrameLoad(tab.key)"></iframe>
    </div>
  </div>
</template>

<script>
/**
 * 决策智能：供应链决策智能体的四个模块。
 *
 * 四个模块是同一套静态多页应用的不同入口（/data、/solve、/forecast、/emergency），
 * 每个入口只渲染一个模块、不带顶部导航，正好一个模块一个 iframe。
 * Tab 标题取各入口自己的页面标题——见该应用 static/app.js 里的 PAGE_META
 * （data=数据管理、forecast=市场预测、emergency=应急响应、solve=优化求解），
 * 和浏览器标签页上显示的名字是同一份文案。
 *
 * 两点写死在代码里的原因：
 * 1) 标题：iframe 跨域，运行时读不到对方的 document.title，只能在这里维护；
 * 2) 域名：预览地址带随机 id（8000-xxxxxxxx.code.cosmoplat.cn），
 *    环境重建后如果变了，只改下面这一行即可。
 */
const DECISION_BASE = 'https://8000-6e3e147b48484c3b.code.cosmoplat.cn'

export default {
  name: 'DecisionIntelligence',
  data() {
    return {
      // 默认落在第一个模块
      activeKey: 'data',
      // 已挂载过的模块（懒加载：点开才建 iframe）
      visited: { data: true },
      // 已加载完成的模块（用来收掉 loading）
      loaded: {},
      tabs: [
        { key: 'data', title: '数据管理', icon: 'el-icon-s-data', url: DECISION_BASE + '/data' },
        { key: 'solve', title: '优化求解', icon: 'el-icon-cpu', url: DECISION_BASE + '/solve' },
        { key: 'forecast', title: '市场预测', icon: 'el-icon-data-line', url: DECISION_BASE + '/forecast' },
        { key: 'emergency', title: '应急响应', icon: 'el-icon-warning-outline', url: DECISION_BASE + '/emergency' }
      ]
    }
  },
  computed: {
    mountedTabs() {
      return this.tabs.filter((tab) => this.visited[tab.key])
    }
  },
  methods: {
    handleTabClick(key) {
      if (this.activeKey === key) return
      this.activeKey = key
      if (!this.visited[key]) this.visited = { ...this.visited, [key]: true }
    },
    handleFrameLoad(key) {
      if (this.loaded[key]) return
      this.loaded = { ...this.loaded, [key]: true }
    }
  }
}
</script>

<style lang="scss" scoped>
.decision-intelligence {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  gap: 16px;
}

/* 二级 Tab：与 SmartDashboard 的二级 Tab 保持同一套观感 */
.sub-tabs {
  display: flex;
  background: #f5f7fa;
  border-radius: 8px;
  padding: 4px;
  flex-shrink: 0;
}

.sub-tab-item {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 6px;
  font-size: 14px;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;

  &:hover {
    color: #4a5ff3;
  }

  &.active {
    background: #fff;
    color: #4a5ff3;
    box-shadow: 0 2px 8px rgba(74, 95, 243, 0.15);
    font-weight: 600;
  }

  i {
    font-size: 16px;
  }
}

.sub-tab-body {
  position: relative;
  flex: 1;
  min-height: 0;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
}

.di-frame {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
}
</style>
